import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import logo from "../assets/logo.png";
import {
    LayoutDashboard,
    FilePlus,
    SquarePen,
    Ticket,
    User,
    Circle,
    Trash,
    PartyPopper,
    Settings,
    Building2,
    MessageCircle,
    Workflow,
    Sigma,
    FlaskConical,
    BriefcaseBusiness,
    ChevronDown,
    ExternalLink,
} from "lucide-react";

const Layout = () => {
    const location = useLocation();
    const { logout } = useKindeAuth();
    const [open, setOpen] = useState(false);

    const [auth, setAuth] = useState(() => ({
        username: localStorage.getItem("username"),
        email: localStorage.getItem("email"),
        role: localStorage.getItem("role"),
    }));

    const syncAuthFromStorage = () => {
        setAuth({
            username: localStorage.getItem("username"),
            email: localStorage.getItem("email"),
            role: localStorage.getItem("role"),
        });
    };

    useEffect(() => {
        syncAuthFromStorage(); // initial
        window.addEventListener("authUpdated", syncAuthFromStorage);
        window.addEventListener("storage", syncAuthFromStorage); // multi-tab support
        return () => {
            window.removeEventListener("authUpdated", syncAuthFromStorage);
            window.removeEventListener("storage", syncAuthFromStorage);
        };
    }, []);

    // Close drawer on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const signOut = () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("role");
        window.dispatchEvent(new Event("authUpdated"));
        logout();
    };

    const quickLinks = [
        { label: "Intranet", url: "https://www.ciilock.com/catalogues/intranet/0212intra0au07", icon: Building2 },
        { label: "Chat", url: "https://chat.ciilock.com", icon: MessageCircle },
        { label: "Enhance", url: "https://enhance.ciilock.com", icon: Workflow },
        { label: "Euler", url: "https://euler.ciilock.com", icon: Sigma },
        { label: "Lab", url: "https://lab.siigmatic.com.au", icon: FlaskConical },
        { label: "Talent", url: "https://talent.ciilock.com", icon: BriefcaseBusiness },
    ];

    const menuItems = useMemo(() => {
        return [
            { path: "/dashboard", name: "Dashboard" },
            { path: "/create-ticket", name: "Create Ticket" },
            { path: "/draft-ticket", name: "My Draft" },
            { path: "/pending-ticket", name: "Pending" },
            { path: "/completed-ticket", name: "Completed" },
            { path: "/abandoned-ticket", name: "Abandoned" },
            ...(auth.role === "admin" ? [{ path: "/setting", name: "Setting" }] : []),
        ];
    }, [auth.role]);

    const iconMap = {
        "/dashboard": LayoutDashboard,
        "/create-ticket": FilePlus,
        "/draft-ticket": SquarePen,
        "/pending-ticket": Ticket,
        "/completed-ticket": PartyPopper,
        "/abandoned-ticket": Trash,
        "/setting": Settings,
        "/profile": User,
    };

    const QuickLinksMenu = ({ compact = false }) => {
        const [quickMenuOpen, setQuickMenuOpen] = useState(false);

        useEffect(() => {
            if (!quickMenuOpen) return undefined;
            const handleOutsideClick = (event) => {
                const root = event.target.closest("[data-quick-links-root]");
                if (!root) setQuickMenuOpen(false);
            };
            window.addEventListener("mousedown", handleOutsideClick);
            return () => window.removeEventListener("mousedown", handleOutsideClick);
        }, [quickMenuOpen]);

        const openLink = (url) => {
            window.open(url, "_blank", "noopener,noreferrer");
            setQuickMenuOpen(false);
        };

        return (
            <div data-quick-links-root className="relative w-full">
                <button
                    type="button"
                    onClick={() => setQuickMenuOpen((prev) => !prev)}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 w-full"
                >
                    <img src={logo} alt="Logo" className={compact ? "h-6 w-6" : "h-7 w-7"} />
                    <div className="text-left leading-tight">
                        <div className={`${compact ? "text-sm" : "text-base"} font-bold text-slate-900`}>Kaizen</div>
                        {!compact && <div className="text-[11px] font-medium text-slate-500">Quick Links</div>}
                    </div>
                    <ChevronDown
                        className={`h-4 w-4 text-slate-500 transition-transform ${quickMenuOpen ? "rotate-180" : ""}`}
                    />
                </button>

                {quickMenuOpen && (
                    <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                        <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Click to navigate
                        </div>
                        <ul className="p-2">
                            {quickLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <li key={link.label}>
                                        <button
                                            type="button"
                                            onClick={() => openLink(link.url)}
                                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-100"
                                        >
                                            <Icon className="h-4 w-4 text-slate-500" />
                                            <span className="flex-1 text-sm font-semibold text-slate-800">
                                                {link.label}
                                            </span>
                                            <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const Sidebar = (
        <aside className="w-72 max-w-[18rem] bg-kaizen-gray text-black flex flex-col h-full" aria-label="Primary">
            <div className="flex items-center p-4 justify-center">
                <QuickLinksMenu />
            </div>

            <nav className="mt-4 flex-1 overflow-y-auto">
                <ul className="flex flex-col items-center gap-2 px-4 pb-4">
                    {menuItems.map(({ path, name }, i) => {
                        const active = location.pathname === path;
                        const Icon = iconMap[path] || Circle;
                        return (
                            <li key={`${path}-${i}`} className="w-full flex justify-center">
                                <Link
                                    to={path}
                                    className={`flex w-full items-center justify-start gap-2 rounded-full px-4 py-2 text-sm font-medium text-left transition-colors duration-150 border
                    ${active
                                            ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                                        }`}
                                >
                                    <Icon
                                        className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"}`}
                                        aria-hidden="true"
                                    />
                                    <span className="truncate">{name}</span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="p-4 border-t border-slate-200">
                <div className="flex flex-col items-center text-center">
                    <div className="mb-1 font-semibold text-md">
                        {auth.username || "User"}
                    </div>
                    <div className="mb-3 text-sm text-slate-600 break-all">
                        {auth.email || "Email"}
                    </div>
                    <button
                        onClick={signOut}
                        type="button"
                        className="w-full max-w-[12rem] px-4 py-2 rounded-lg bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </aside>
    );

    return (
        <div className="h-screen w-full flex bg-gray-100">
            {/* Mobile top bar */}
            <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-kaizen-gray text-black h-16">
                <div className="flex items-center justify-between p-4">
                    <button
                        aria-label="Open navigation"
                        aria-expanded={open}
                        aria-controls="mobile-sidebar"
                        onClick={() => setOpen(true)}
                        className="p-2 focus:outline-none focus:ring-2 focus:ring-white/60 rounded"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>

                    <div className="flex items-center gap-3">
                        <QuickLinksMenu compact />
                    </div>

                    <span className="text-sm font-medium">{auth.username || "User"}</span>
                </div>
            </header>

            {/* Desktop sidebar */}
            <div className="hidden md:flex md:flex-col md:h-full md:w-72 md:shrink-0">{Sidebar}</div>

            {/* Mobile drawer + overlay */}
            <div id="mobile-sidebar" className={`fixed inset-0 z-50 md:hidden ${open ? "" : "pointer-events-none"}`}>
                <div
                    className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />
                <div
                    className={`absolute left-0 top-0 h-full transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="h-full w-72 bg-kaizen-gray shadow-xl relative">
                        <button
                            aria-label="Close navigation"
                            onClick={() => setOpen(false)}
                            className="absolute right-3 top-3 p-2 rounded focus:outline-none focus:ring-2 focus:ring-white/60"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                        {Sidebar}
                    </div>
                </div>
            </div>

            <main className="flex-1 overflow-y-auto w-full pt-16 md:pt-0">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
